/**
 * Date formatting helpers for EximFlow.
 *
 * The app stores order dates internally as `DDMMYY` (e.g. "120326" = 12 Mar 2026).
 * Other backends emit ISO (`2026-03-12`), datetime (`2026-03-12T10:53:40+00:00`),
 * or Excel-locale strings (`12/03/2026`). Every UI surface should render those
 * the same way: `DD/MM/YYYY`.
 *
 * `formatDMY(input, { compact })` accepts every variant we've seen in the
 * codebase and returns either `DD/MM/YYYY` (default) or `DDMMYY` (compact).
 */

const ORDER_DATE_RE = /^(\d{2})(\d{2})(\d{2})$/;        // DDMMYY
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})/;              // 2026-03-12 or 2026-03-12T...
const SLASH_RE = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/;

function pad(n) {
  return String(n).padStart(2, "0");
}

/**
 * Parse any of the common shapes into {d, m, y4} where y4 is a 4-digit year.
 * Returns null if the input is empty / unparsable.
 */
export function parseAnyDate(input) {
  if (input === null || input === undefined || input === "") return null;

  // Date instance
  if (input instanceof Date && !Number.isNaN(input.getTime())) {
    return { d: input.getDate(), m: input.getMonth() + 1, y4: input.getFullYear() };
  }

  const s = String(input).trim();
  if (!s) return null;

  // 2026-03-12[T...] → ISO
  const iso = s.match(ISO_RE);
  if (iso) {
    return { d: parseInt(iso[3], 10), m: parseInt(iso[2], 10), y4: parseInt(iso[1], 10) };
  }

  // DDMMYY (the canonical internal `order_date`)
  const ord = s.match(ORDER_DATE_RE);
  if (ord) {
    const d = parseInt(ord[1], 10);
    const m = parseInt(ord[2], 10);
    const yy = parseInt(ord[3], 10);
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12) {
      // 00-69 → 2000-2069, 70-99 → 1970-1999 (consistent with most apps)
      const y4 = yy >= 70 ? 1900 + yy : 2000 + yy;
      return { d, m, y4 };
    }
  }

  // DD/MM/YYYY or DD-MM-YYYY (treated DMY because that's the app's convention)
  const sl = s.match(SLASH_RE);
  if (sl) {
    const d = parseInt(sl[1], 10);
    const m = parseInt(sl[2], 10);
    let y = parseInt(sl[3], 10);
    if (y < 100) y = y >= 70 ? 1900 + y : 2000 + y;
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12) {
      return { d, m, y4: y };
    }
  }

  // Last resort: let the JS Date constructor try
  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) {
    return { d: dt.getDate(), m: dt.getMonth() + 1, y4: dt.getFullYear() };
  }

  return null;
}

/**
 * Format any incoming date-ish value as `DDMMYY` (default, the app's canonical
 * internal date format) or `DD/MM/YYYY` (when `expanded: true` is passed).
 * Returns the fallback string (default `"-"`) when the input can't be parsed.
 */
export function formatDMY(input, opts = {}) {
  const { compact = true, expanded = false, fallback = "-" } = opts;
  const p = parseAnyDate(input);
  if (!p) return fallback;
  if (expanded || compact === false) {
    return `${pad(p.d)}/${pad(p.m)}/${p.y4}`;
  }
  return `${pad(p.d)}${pad(p.m)}${String(p.y4).slice(-2)}`;
}

/**
 * Convert any date-ish value to ISO `YYYY-MM-DD`.
 * Useful when feeding values back to a backend that expects ISO.
 */
export function toISO(input) {
  const p = parseAnyDate(input);
  if (!p) return "";
  return `${p.y4}-${pad(p.m)}-${pad(p.d)}`;
}
